import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import UserProfileDropdown from '../../components/ui/UserProfileDropdown';
import HeroSection from './components/HeroSection';
import CapabilitiesSection from './components/CapabilitiesSection';
import HowItWorksSection from './components/HowItWorksSection';
import CTASection from './components/CTASection';
import FooterSection from './components/FooterSection';
import PricingSection from './components/PricingSection';
// Pilot disabled: reviews/testimonials section retained for post-pilot restore.
// import TestimonialsSection from './components/TestimonialsSection';
import BlogsSection from './components/BlogsSection';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useAuth } from '../../contexts/AuthContext';
import './landing.css';
import { useDiscountCampaign } from '../../components/DiscountBanner';

const navLinks = [
  { href: '#features', label: 'Product' },
  { href: '#how-it-works', label: 'Process' },
  // 'For Institutions' now lives in the dropdown next to the logo (see header).
  // { href: '/for-institutions', label: 'For Institutions', isRoute: true },
  { href: '/resume-analyzer', label: 'ATS', isRoute: true },
  // { href: '/for-businesses', label: 'for hiring', isRoute: true },
  // Pilot disabled: reviews navigation hidden during restricted pilot.
  // { href: '/review', label: 'Reviews', isRoute: true },
  { href: '#pricing', label: 'Pricing' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const { isEnabled } = useFeatureFlags();
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const discountCampaign = useDiscountCampaign();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [instMenuOpen, setInstMenuOpen] = useState(false);
  const instMenuRef = useRef(null);

  useEffect(() => {
    if (!instMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (instMenuRef.current && !instMenuRef.current.contains(event.target)) {
        setInstMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setInstMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [instMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      setScrolled(window.scrollY > 24);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleSignOut = async () => {
    await logout();
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  const toggleTheme = () => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    if (isDarkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
      setIsDark(false);
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
      setIsDark(true);
    }
  };

  return (
    <div className="landing-page min-h-screen bg-[var(--lp-bg)] text-[var(--lp-ink)] transition-colors duration-300">
      <header
        className={`fixed left-0 right-0 z-[100] transition-all duration-300 ${discountCampaign ? 'top-7' : 'top-0'} ${
          scrolled ? 'border-b border-[var(--lp-line)] bg-[var(--lp-bg)]/90 backdrop-blur-md' : 'bg-transparent'
        }`}
      >
        <div className="relative mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
          <div ref={instMenuRef} className="relative flex items-center gap-1.5">
            <Link to="/" className="text-xl font-semibold tracking-tight text-[var(--lp-ink)]">
              prepx<span className="text-[var(--lp-accent)]">ai</span>
            </Link>
            
            {/* <button
              type="button"
              onClick={() => setInstMenuOpen((open) => !open)}
              className="flex h-6 w-6 items-center justify-center rounded-md text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              aria-label="Open menu"
              aria-haspopup="menu"
              aria-expanded={instMenuOpen}
            >
              <Icon
                name="ChevronDown"
                size={16}
                className={`transition-transform duration-200 ${instMenuOpen ? 'rotate-180' : ''}`}
              />
            </button> */}

            {instMenuOpen && (
              <div
                role="menu"
                className="absolute left-0 top-full z-50 mt-2 min-w-[180px] overflow-hidden rounded-xl border border-black/10 bg-white py-1 shadow-lg"
              >
                <Link
                  to="/for-institutions"
                  role="menuitem"
                  onClick={() => setInstMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-100"
                >
                  <Icon name="Building" size={14} className="text-gray-500" />
                  Institutions
                </Link>
                <Link
                  to="/for-businesses"
                  role="menuitem"
                  onClick={() => setInstMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-medium text-gray-900 transition-colors hover:bg-gray-100"
                >
                  <Icon name="Building2" size={14} className="text-gray-500" />
                  Hiring
                </Link>
              </div>
            )}
          </div>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-mono text-[11px] font-semibold tracking-wider text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)]"
              aria-label="Toggle theme"
            >
              <Icon name={isDark ? 'Sun' : 'Moon'} size={16} />
            </button>

            {isAuthenticated ? (
              <UserProfileDropdown user={user} onSignOut={handleSignOut} />
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => openAuthModal('login')}
                  className="hidden text-sm font-medium text-[var(--lp-ink-soft)] transition-colors hover:text-[var(--lp-ink)] sm:inline-flex"
                >
                  Log in
                </button>
                <Button
                  size="sm"
                  onClick={() => openAuthModal('register', { redirectTo: '/interview-setup' })}
                  className="hidden h-9 rounded-full bg-[var(--lp-accent)] px-5 text-xs font-semibold text-white hover:bg-[var(--lp-accent)]/90 sm:inline-flex"
                >
                  Create account
                </Button>
              </>
            )}

            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--lp-line)] md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label="Menu"
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={18} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-[var(--lp-line)] bg-[var(--lp-bg)] px-5 py-4 md:hidden">
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
                  >
                    {link.label}
                  </a>
                )
              ))}
              <Link
                to="/for-institutions"
                onClick={() => setMobileOpen(false)}
                className="font-mono text-xs font-semibold tracking-wider text-[var(--lp-ink)]"
              >
                Institutions
              </Link>
              <button
                type="button"
                onClick={() => { setMobileOpen(false); openAuthModal('register', { redirectTo: '/interview-setup' }); }}
                className="mt-2 h-10 rounded-full bg-[var(--lp-accent)] text-sm font-semibold text-white hover:bg-[var(--lp-accent)]/90"
              >
                Create account
              </button>
            </nav>
          </div>
        )}
      </header>

      <main>
        <HeroSection />
        <CapabilitiesSection />
        <HowItWorksSection />
        {/* Pilot disabled: reviews/testimonials section hidden during restricted pilot.
        <TestimonialsSection />
        */}
        <PricingSection />
        <FAQSection />
        {isEnabled('blog') && <BlogsSection />}
        <CTASection />
        <FooterSection />
      </main>

      {showScrollTop && (
        <Button
          variant="default"
          size="icon"
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-[90] h-10 w-10 rounded-full border border-[var(--lp-line)] bg-[var(--lp-surface)] text-[var(--lp-ink)] shadow-lg"
          aria-label="Scroll to top"
        >
          <Icon name="ArrowUp" size={18} />
        </Button>
      )}
    </div>
  );
};

export default LandingPage;
